import { apiFetch, setAccessToken } from "@/services/api-client";
import type { TokenResponse, User } from "@/types/api";

export interface LoginCredentials {
  username: string;
  password: string;
}

export async function login(credentials: LoginCredentials): Promise<User> {
  const token = await apiFetch<TokenResponse>("/auth/login", {
    method: "POST",
    body: credentials,
    // A failed login is a real 401, not an expired token; refreshing would be
    // pointless and would mask the error.
    skipRefresh: true,
  });
  setAccessToken(token.access_token);
  return getCurrentUser();
}

export async function logout(): Promise<void> {
  try {
    await apiFetch<{ message: string }>("/auth/logout", {
      method: "POST",
      skipRefresh: true,
    });
  } finally {
    // Drop the in-memory token even if the network call failed, so the UI never
    // shows a signed-in state the server no longer honours.
    setAccessToken(null);
  }
}

export async function getCurrentUser(): Promise<User> {
  return apiFetch<User>("/users/me");
}

export async function requestAccess(input: {
  full_name: string;
  email: string;
  phone: string;
  privacy_accepted: boolean;
}): Promise<void> {
  await apiFetch<{ message: string }>("/auth/access-requests", {
    method: "POST",
    body: input,
    skipRefresh: true,
  });
}

export async function forgotPassword(username: string): Promise<void> {
  // The server always answers the same way; a short-lived code is e-mailed
  // when the account exists (see backend request_password_reset).
  await apiFetch<{ message: string }>("/auth/forgot-password", {
    method: "POST",
    body: { username },
    skipRefresh: true,
  });
}

export async function resetPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<void> {
  // The e-mailed code is the credential; the caller signs in afterwards.
  await apiFetch<{ message: string }>("/auth/reset-password", {
    method: "POST",
    body: { username, code, new_password: newPassword },
    skipRefresh: true,
  });
}

export async function updateProfile(input: {
  full_name?: string;
  phone?: string;
  home_area_id?: string;
}): Promise<User> {
  return apiFetch<User>("/users/me", { method: "PATCH", body: input });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiFetch<{ message: string }>("/auth/change-password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
  // The server revokes every session on a password change; the caller signs
  // in again with the new credential to continue seamlessly.
  setAccessToken(null);
}

export async function closeAccount(password: string): Promise<void> {
  // Re-authenticates: a live session alone must not be able to close the
  // account. The server revokes every session; the caller signs out to drop
  // the in-memory token, the cookie and the query cache.
  await apiFetch<{ message: string }>("/users/me/deactivate", {
    method: "POST",
    body: { password },
  });
}
